import { useState, type FormEvent } from 'react';
import { normTags, STOCK_TAGS } from '../lib/projectJobs.js';
import { humanize } from '../lifeos/format.js';

/** A person's tags as small read-only chips ("Friend", "Client"). */
export function TagList({ tags }: { tags: readonly string[] | undefined }): JSX.Element | null {
  if (!tags || tags.length === 0) return null;
  return (
    <span className="tag-list">
      {tags.map((t) => (
        <span key={t} className={`tag-chip tag-chip--${t.replace(/[^a-z0-9-]/g, '')}`}>
          {humanize(t)}
        </span>
      ))}
    </span>
  );
}

/**
 * Tag editor: the stock tags as big toggles, any custom tags the person has,
 * and a field to add another. Every change calls `onChange` with the full list.
 */
export function TagEditor({ tags, onChange }: { tags: readonly string[]; onChange: (next: string[]) => void }): JSX.Element {
  const [custom, setCustom] = useState('');
  const all = normTags([...STOCK_TAGS, ...tags]);
  const has = new Set(tags);
  const toggle = (t: string): void => onChange(has.has(t) ? tags.filter((x) => x !== t) : normTags([...tags, t]));
  const add = (e: FormEvent): void => {
    e.preventDefault();
    const t = normTags([custom])[0];
    setCustom('');
    if (t && !has.has(t)) onChange([...tags, t]);
  };
  return (
    <div className="tag-edit">
      <div className="tag-edit__chips" role="group" aria-label="Tags">
        {all.map((t) => (
          <button
            key={t}
            type="button"
            className={`tag-toggle${has.has(t) ? ' tag-toggle--on' : ''}`}
            aria-pressed={has.has(t)}
            onClick={() => toggle(t)}
          >
            {humanize(t)}
          </button>
        ))}
      </div>
      <form className="tag-edit__add" onSubmit={add}>
        <input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Add a tag" aria-label="New tag" maxLength={40} />
        {custom.trim() && (
          <button type="submit" className="la-addrow__save">
            Add
          </button>
        )}
      </form>
    </div>
  );
}
